import { useCallback } from 'react';

import type { SkillProficiencyLevel } from '~/shared/data-contract/skill';
import type { LanguageCodeForJSA } from '~/shared/internationalization';

import { createSkillDetailTilePayload, SKILL_DETAIL_TILE_SLOT_KEY } from '../../skills';
import { type UrlTilePayload, useUrlTiles } from '../../url-tiles';
import { useJobArchitectureContentLanguage } from '../providers/JobArchitectureLanguageProvider';
import { useActiveJobArchitectureId } from '../url-navigation/job-architecture-scope';
import { useUrlSelection } from '../url-navigation/use-url-selection';
import {
    createJsaArchiveTilePayload,
    createJsaChangelogTilePayload,
    createJsaClusterDetailTilePayload,
    createJsaCompareTilePayload,
    createJsaFamilyDetailTilePayload,
    createJsaLevelComparisonTilePayload,
    createJsaProfileDetailTilePayload,
    createJsaRoleComparisonTilePayload,
    isJsaCompareTilePayload,
    isJsaLevelComparisonTilePayload,
    isJsaProfileDetailTilePayload,
    isJsaRoleComparisonTilePayload,
    JSA_ARCHIVE_TILE_SLOT_KEY,
    JSA_CHANGELOG_TILE_SLOT_KEY,
    JSA_CLUSTER_DETAIL_TILE_TYPE,
    JSA_COMPARE_TILE_SLOT_KEY,
    JSA_FAMILY_DETAIL_TILE_TYPE,
    JSA_LEVEL_COMPARISON_TILE_SLOT_KEY,
    JSA_PRIMARY_DETAIL_TILE_SLOT_KEY,
    JSA_PROFILE_DETAIL_TILE_TYPE,
    JSA_ROLE_COMPARISON_TILE_SLOT_KEY,
    type JsaRoleComparisonTilePayload,
    selectJsaPrimaryDetailNotificationRowFromTile,
    withJsaProfileCommentsOpen,
} from '../url-tile-payloads';

type JsaPrimaryDetailTarget =
    | {
          readonly familyId: string;
          readonly type: 'job-family';
      }
    | {
          readonly clusterId: string;
          readonly familyId: string;
          readonly type: 'job-cluster';
      }
    | {
          readonly profileId: string;
          readonly type: 'job-level' | 'job-role';
      };

const selectMatchingNotificationRow = (
    payload: UrlTilePayload | undefined,
    architectureId: string,
    target: JsaPrimaryDetailTarget,
): string | undefined => {
    if (payload?.['architectureId'] !== architectureId) {
        return undefined;
    }

    switch (target.type) {
        case 'job-family':
            return payload.type === JSA_FAMILY_DETAIL_TILE_TYPE && payload['familyId'] === target.familyId
                ? selectJsaPrimaryDetailNotificationRowFromTile(payload)
                : undefined;
        case 'job-cluster':
            return payload.type === JSA_CLUSTER_DETAIL_TILE_TYPE &&
                payload['clusterId'] === target.clusterId &&
                payload['familyId'] === target.familyId
                ? selectJsaPrimaryDetailNotificationRowFromTile(payload)
                : undefined;
        case 'job-level':
        case 'job-role':
            return payload.type === JSA_PROFILE_DETAIL_TILE_TYPE &&
                payload['itemType'] === target.type &&
                payload['profileId'] === target.profileId
                ? selectJsaPrimaryDetailNotificationRowFromTile(payload)
                : undefined;
    }
};

type OpenJobProfileTileParams = {
    readonly isArchived?: boolean;
    readonly mode: 'edit' | 'view';
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly profileId: string;
    readonly type: 'job-level' | 'job-role';
    readonly version?: number;
};

type OpenCompareTileParams = {
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly profileId: string;
    readonly proposalBlockId?: string;
    readonly type: 'job-level' | 'job-role';
    readonly versionA: string;
    readonly versionB: string;
};

type OpenLevelComparisonTileParams = {
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly roleId: string;
    readonly showProficiency?: boolean;
};

type OpenRoleComparisonTileParams = {
    readonly itemIds: readonly string[];
    readonly itemType: 'job-level' | 'job-role';
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly showProficiency?: boolean;
};

export const useJobArchitectureUrlTileActions = () => {
    const architectureId = useActiveJobArchitectureId();
    const contentLanguage = useJobArchitectureContentLanguage();
    const { setSelectedClusterId, setSelectedFamilyId } = useUrlSelection();
    const { closeTile, tiles, updateTile } = useUrlTiles();

    const hasTileInSlot = useCallback(
        (slotKey: string): boolean => tiles.some((tile) => tile.slotKey === slotKey),
        [tiles],
    );

    const hasPrimaryDetailTile = useCallback(
        (): boolean => hasTileInSlot(JSA_PRIMARY_DETAIL_TILE_SLOT_KEY),
        [hasTileInSlot],
    );

    const hasCompareTile = useCallback((): boolean => hasTileInSlot(JSA_COMPARE_TILE_SLOT_KEY), [hasTileInSlot]);

    const hasChangelogTile = useCallback((): boolean => hasTileInSlot(JSA_CHANGELOG_TILE_SLOT_KEY), [hasTileInSlot]);

    const hasArchiveTile = useCallback((): boolean => hasTileInSlot(JSA_ARCHIVE_TILE_SLOT_KEY), [hasTileInSlot]);

    const hasLevelComparisonTile = useCallback(
        (): boolean => hasTileInSlot(JSA_LEVEL_COMPARISON_TILE_SLOT_KEY),
        [hasTileInSlot],
    );

    const hasRoleComparisonTile = useCallback(
        (): boolean => hasTileInSlot(JSA_ROLE_COMPARISON_TILE_SLOT_KEY),
        [hasTileInSlot],
    );

    const hasSkillTile = useCallback((): boolean => hasTileInSlot(SKILL_DETAIL_TILE_SLOT_KEY), [hasTileInSlot]);

    const getMatchingNotificationRow = useCallback(
        (target: JsaPrimaryDetailTarget): string | undefined =>
            selectMatchingNotificationRow(
                tiles.find((tile) => tile.slotKey === JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)?.payload,
                architectureId,
                target,
            ),
        [architectureId, tiles],
    );

    const openFamilyTile = useCallback(
        (familyId: string, mode: 'edit' | 'view'): void => {
            setSelectedFamilyId(familyId);
            updateTile(
                createJsaFamilyDetailTilePayload({
                    architectureId,
                    familyId,
                    mode,
                    notificationRow: getMatchingNotificationRow({ familyId, type: 'job-family' }),
                    preferredLanguage: contentLanguage,
                }),
            );
        },
        [architectureId, contentLanguage, getMatchingNotificationRow, setSelectedFamilyId, updateTile],
    );

    const openClusterTile = useCallback(
        (clusterId: string, familyId: string, mode: 'edit' | 'view'): void => {
            setSelectedFamilyId(familyId);
            setSelectedClusterId(clusterId);
            updateTile(
                createJsaClusterDetailTilePayload({
                    architectureId,
                    clusterId,
                    familyId,
                    mode,
                    notificationRow: getMatchingNotificationRow({ clusterId, familyId, type: 'job-cluster' }),
                    preferredLanguage: contentLanguage,
                }),
            );
        },
        [
            architectureId,
            contentLanguage,
            getMatchingNotificationRow,
            setSelectedClusterId,
            setSelectedFamilyId,
            updateTile,
        ],
    );

    const openJobProfileTile = useCallback(
        ({ isArchived, mode, preferredLanguage, profileId, type, version }: OpenJobProfileTileParams): void => {
            const primaryPayload = tiles.find((tile) => tile.slotKey === JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)?.payload;
            // Paging between profiles replaces the payload wholesale, and the comments-open flag is
            // the only place that state lives now that the rail no longer holds its own. Carrying it
            // over keeps the comment tile open across next/previous instead of closing on every step.
            const keepCommentsOpen =
                isJsaProfileDetailTilePayload(primaryPayload) && primaryPayload.isCommentsOpen === true;

            closeTile(SKILL_DETAIL_TILE_SLOT_KEY);
            updateTile(
                createJsaProfileDetailTilePayload({
                    architectureId,
                    isArchived,
                    ...(keepCommentsOpen ? { isCommentsOpen: true } : {}),
                    itemType: type,
                    mode,
                    notificationRow: getMatchingNotificationRow({ profileId, type }),
                    preferredLanguage: preferredLanguage ?? contentLanguage,
                    profileId,
                    version,
                }),
            );
        },
        [architectureId, closeTile, contentLanguage, getMatchingNotificationRow, tiles, updateTile],
    );

    const openCompareTile = useCallback(
        ({ preferredLanguage, profileId, proposalBlockId, type, versionA, versionB }: OpenCompareTileParams): void => {
            updateTile(
                createJsaCompareTilePayload({
                    architectureId,
                    itemType: type,
                    preferredLanguage: preferredLanguage ?? contentLanguage,
                    profileId,
                    proposalBlockId,
                    versionA,
                    versionB,
                }),
            );
        },
        [architectureId, contentLanguage, updateTile],
    );

    const openChangelogTile = useCallback((): void => {
        updateTile(createJsaChangelogTilePayload({ architectureId, preferredLanguage: contentLanguage }));
    }, [architectureId, contentLanguage, updateTile]);

    const openArchiveTile = useCallback((): void => {
        updateTile(createJsaArchiveTilePayload({ architectureId }));
    }, [architectureId, updateTile]);

    const openLevelComparisonTile = useCallback(
        ({ preferredLanguage, roleId, showProficiency }: OpenLevelComparisonTileParams): void => {
            updateTile(
                createJsaLevelComparisonTilePayload({
                    architectureId,
                    preferredLanguage: preferredLanguage ?? contentLanguage,
                    roleId,
                    showProficiency,
                }),
            );
        },
        [architectureId, contentLanguage, updateTile],
    );

    const openRoleComparisonTile = useCallback(
        ({ itemIds, itemType, preferredLanguage, showProficiency }: OpenRoleComparisonTileParams): void => {
            updateTile(
                createJsaRoleComparisonTilePayload({
                    architectureId,
                    itemIds,
                    itemType,
                    preferredLanguage: preferredLanguage ?? contentLanguage,
                    showProficiency,
                }),
            );
        },
        [architectureId, contentLanguage, updateTile],
    );

    const openSkillTile = useCallback(
        (skillId: string, preferredLanguage?: LanguageCodeForJSA, proficiency?: SkillProficiencyLevel | null): void => {
            updateTile(
                createSkillDetailTilePayload({
                    preferredLanguage: preferredLanguage ?? contentLanguage,
                    proficiency: proficiency ?? undefined,
                    skillId,
                }),
            );
        },
        [contentLanguage, updateTile],
    );

    const closePrimaryDetailTile = useCallback((): void => {
        closeTile(JSA_PRIMARY_DETAIL_TILE_SLOT_KEY);
    }, [closeTile]);

    const closeCompareTile = useCallback((): void => {
        closeTile(JSA_COMPARE_TILE_SLOT_KEY);
    }, [closeTile]);

    const closeChangelogTile = useCallback((): void => {
        closeTile(JSA_CHANGELOG_TILE_SLOT_KEY);
    }, [closeTile]);

    const closeArchiveTile = useCallback((): void => {
        closeTile(JSA_ARCHIVE_TILE_SLOT_KEY);
    }, [closeTile]);

    const closeLevelComparisonTile = useCallback((): void => {
        closeTile(JSA_LEVEL_COMPARISON_TILE_SLOT_KEY);
    }, [closeTile]);

    const closeRoleComparisonTile = useCallback((): void => {
        closeTile(JSA_ROLE_COMPARISON_TILE_SLOT_KEY);
    }, [closeTile]);

    const closeSkillTile = useCallback((): void => {
        closeTile(SKILL_DETAIL_TILE_SLOT_KEY);
    }, [closeTile]);

    const updateJobProfileTileLanguage = useCallback(
        (preferredLanguage: LanguageCodeForJSA): void => {
            const primaryPayload = tiles.find((tile) => tile.slotKey === JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)?.payload;

            if (isJsaProfileDetailTilePayload(primaryPayload)) {
                updateTile({ ...primaryPayload, preferredLanguage });
            }
        },
        [tiles, updateTile],
    );

    const updateJobProfileTileCommentsOpen = useCallback(
        (isCommentsOpen: boolean): void => {
            const primaryPayload = tiles.find((tile) => tile.slotKey === JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)?.payload;

            if (!isJsaProfileDetailTilePayload(primaryPayload)) {
                return;
            }

            if (isCommentsOpen) {
                if (primaryPayload.isCommentsOpen === true) {
                    return;
                }
                updateTile(withJsaProfileCommentsOpen(primaryPayload, true));
                return;
            }

            if (primaryPayload.isCommentsOpen === undefined) {
                return;
            }

            updateTile(withJsaProfileCommentsOpen(primaryPayload, false));
        },
        [tiles, updateTile],
    );

    const toggleJobProfileTileCommentsOpen = useCallback((): void => {
        const primaryPayload = tiles.find((tile) => tile.slotKey === JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)?.payload;

        if (!isJsaProfileDetailTilePayload(primaryPayload)) {
            return;
        }

        updateTile(withJsaProfileCommentsOpen(primaryPayload, primaryPayload.isCommentsOpen !== true));
    }, [tiles, updateTile]);

    const updateCompareTileLanguage = useCallback(
        (preferredLanguage: LanguageCodeForJSA): void => {
            const comparePayload = tiles.find((tile) => tile.slotKey === JSA_COMPARE_TILE_SLOT_KEY)?.payload;

            if (isJsaCompareTilePayload(comparePayload)) {
                updateTile({ ...comparePayload, preferredLanguage });
            }
        },
        [tiles, updateTile],
    );

    const updateLevelComparisonTileLanguage = useCallback(
        (preferredLanguage: LanguageCodeForJSA): void => {
            const comparisonPayload = tiles.find(
                (tile) => tile.slotKey === JSA_LEVEL_COMPARISON_TILE_SLOT_KEY,
            )?.payload;

            if (isJsaLevelComparisonTilePayload(comparisonPayload)) {
                updateTile({ ...comparisonPayload, preferredLanguage });
            }
        },
        [tiles, updateTile],
    );

    const updateLevelComparisonTileProficiency = useCallback(
        (showProficiency: boolean): void => {
            const comparisonPayload = tiles.find(
                (tile) => tile.slotKey === JSA_LEVEL_COMPARISON_TILE_SLOT_KEY,
            )?.payload;

            if (isJsaLevelComparisonTilePayload(comparisonPayload)) {
                updateTile({ ...comparisonPayload, showProficiency });
            }
        },
        [tiles, updateTile],
    );

    const updateRoleComparisonTile = useCallback(
        (
            patch: Partial<Pick<JsaRoleComparisonTilePayload, 'itemIds' | 'preferredLanguage' | 'showProficiency'>>,
        ): void => {
            const comparisonPayload = tiles.find((tile) => tile.slotKey === JSA_ROLE_COMPARISON_TILE_SLOT_KEY)?.payload;

            if (isJsaRoleComparisonTilePayload(comparisonPayload)) {
                updateTile({ ...comparisonPayload, ...patch });
            }
        },
        [tiles, updateTile],
    );

    const updateRoleComparisonTileLanguage = useCallback(
        (preferredLanguage: LanguageCodeForJSA): void => updateRoleComparisonTile({ preferredLanguage }),
        [updateRoleComparisonTile],
    );

    const updateRoleComparisonTileProficiency = useCallback(
        (showProficiency: boolean): void => updateRoleComparisonTile({ showProficiency }),
        [updateRoleComparisonTile],
    );

    const updateRoleComparisonTileItems = useCallback(
        (itemIds: readonly string[]): void => updateRoleComparisonTile({ itemIds: [...itemIds] }),
        [updateRoleComparisonTile],
    );

    const updateJobProfileTileVersion = useCallback(
        (version: number): void => {
            const primaryPayload = tiles.find((tile) => tile.slotKey === JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)?.payload;

            if (isJsaProfileDetailTilePayload(primaryPayload)) {
                updateTile({ ...primaryPayload, version });
            }
        },
        [tiles, updateTile],
    );

    return {
        closeArchiveTile,
        closeChangelogTile,
        closeCompareTile,
        closeLevelComparisonTile,
        closePrimaryDetailTile,
        closeRoleComparisonTile,
        closeSkillTile,
        hasArchiveTile,
        hasChangelogTile,
        hasCompareTile,
        hasLevelComparisonTile,
        hasPrimaryDetailTile,
        hasRoleComparisonTile,
        hasSkillTile,
        openArchiveTile,
        openChangelogTile,
        openClusterTile,
        openCompareTile,
        openFamilyTile,
        openJobProfileTile,
        openLevelComparisonTile,
        openRoleComparisonTile,
        openSkillTile,
        toggleJobProfileTileCommentsOpen,
        updateCompareTileLanguage,
        updateLevelComparisonTileLanguage,
        updateLevelComparisonTileProficiency,
        updateRoleComparisonTileLanguage,
        updateRoleComparisonTileProficiency,
        updateRoleComparisonTileItems,
        updateJobProfileTileCommentsOpen,
        updateJobProfileTileLanguage,
        updateJobProfileTileVersion,
    };
};
